// ============================================================================
// CUPBOARD
// ============================================================================

import CONFIG from '../config.js';
import { lerp, easeOutElastic } from '../utils/helpers.js';
import { DetachedDoor } from './DetachedDoor.js';
import { BreakableEntity } from './BreakableEntity.js';

export class Cupboard extends BreakableEntity {
    constructor(x, y, physics = null, doorsOpenOutward = false) {
        super(x, y, 40, 60, physics);
        this.doorWidth = 18;  // Each door is slightly less than half width
        this.doorHeight = 50;  // Doors are slightly shorter than body
        this.doorsOpenOutward = doorsOpenOutward; // If true, doors open outward (120 deg), else inward (120 deg)
        
        this.isOpen = false;
        this.isAnimating = false;
        this.doorAngle = 0;
        this.targetAngle = 0;
        this.animationTime = 0;
        this.animationDuration = 0;
        this.wobbleTime = 0;
        this.wobbleAmplitude = 0;
        this.tilt = 0;
        this.overshoot = 0;
        
        // Create Matter.js body (static when not broken)
        if (physics) {
            this.body = physics.createRectangle(x, y, this.width, this.height, {
                isStatic: true,
                frictionAir: 0,
                friction: 0,
                restitution: 0
            });
            physics.linkEntity(this, this.body);
        }
    }

    canInteract() {
        return !this.isAnimating;
    }

    performAction(level, aggression, game) {
        this.isOpen = !this.isOpen;
        // Inward-opening: doors open to 120 degrees (wider than cupboard)
        // Outward-opening: doors swing outward at 70 degrees (like sketch)
        this.targetAngle = this.isOpen ? (this.doorsOpenOutward ? 70 : 120) : 0;

        this.isAnimating = true;
        this.animationTime = 0;
        this.animationDuration = CONFIG.doorOpenSpeed[level];
        this.overshoot = CONFIG.doorOvershoot[level];

        if (level >= 1) {
            this.wobbleAmplitude = level * 2;
            this.wobbleTime = 0.5;
        }

        // Tilt increases with aggression (aggression already starts at 0 after interactionBaselineTime)
        if (aggression > 0) {
            this.tilt += aggression * 0.5;
            this.tilt = Math.min(this.tilt, CONFIG.maxTilt);
        }
    }

    getBreakThreshold() {
        return CONFIG.breakThreshold;
    }

    onBreak(game) {
        // Get the current door color based on damage
        let cupboardColor = CONFIG.colors.cupboard;
        if (this.damage >= CONFIG.cupboardDamageThresholds[2]) {
            cupboardColor = '#4B2010';
        } else if (this.damage >= CONFIG.cupboardDamageThresholds[1]) {
            cupboardColor = '#6B3410';
        } else if (this.damage >= CONFIG.cupboardDamageThresholds[0]) {
            cupboardColor = '#8B4010';
        }

        // Calculate door positions based on current open state
        const openAmount = this.doorAngle / 90;
        const doorOffset = openAmount * (this.doorWidth + 1);

        // Create detached doors with the last damage amount as force
        const leftDoorX = this.x - this.width / 2 - doorOffset;
        const rightDoorX = this.x + this.width / 2 + doorOffset;

        // Cap force amount for spawn velocity (prevents tunneling), but damage was already applied uncapped
        const cappedForce = Math.min(this.lastDamageAmount, CONFIG.throwableMaxAggression);
        
        game.detachedDoors.push(new DetachedDoor(
            leftDoorX,
            this.y,
            this.doorWidth,
            this.doorHeight,
            cupboardColor,
            true,  // isLeftDoor
            cappedForce,
            this.physics
        ));

        game.detachedDoors.push(new DetachedDoor(
            rightDoorX,
            this.y,
            this.doorWidth,
            this.doorHeight,
            cupboardColor,
            false,  // isLeftDoor (right door)
            cappedForce,
            this.physics
        ));
    }

    update(dt) {
        // Sync position from physics body (if not broken)
        if (this.body && !this.isBroken) {
            this.x = this.body.position.x;
            this.y = this.body.position.y;
        }
        
        if (this.isAnimating) {
            this.animationTime += dt;
            const progress = Math.min(this.animationTime / this.animationDuration, 1);

            const easedProgress = easeOutElastic(progress);
            const overshootAngle = this.targetAngle + (this.isOpen ? this.overshoot : -this.overshoot);

            const startAngle = this.isOpen ? 0 : (this.doorsOpenOutward ? 70 : 120);
            if (this.overshoot > 0) {
                if (progress < 0.7) {
                    this.doorAngle = lerp(startAngle, overshootAngle, progress / 0.7);
                } else {
                    this.doorAngle = lerp(overshootAngle, this.targetAngle, (progress - 0.7) / 0.3);
                }
            } else {
                this.doorAngle = lerp(startAngle, this.targetAngle, easedProgress);
            }

            if (progress >= 1) {
                this.doorAngle = this.targetAngle;
                this.isAnimating = false;
            }
        }

        if (this.wobbleTime > 0) {
            this.wobbleTime -= dt;
        }
    }

    draw(ctx, camera) {
        ctx.save();

        let wobbleX = 0;
        let wobbleY = 0;
        if (this.wobbleTime > 0) {
            wobbleX = Math.sin(this.wobbleTime * 30) * this.wobbleAmplitude * (this.wobbleTime / 0.5);
            wobbleY = Math.cos(this.wobbleTime * 30) * this.wobbleAmplitude * (this.wobbleTime / 0.5);
        }

        const drawX = this.x + wobbleX;
        const drawY = this.y + wobbleY;

        ctx.translate(drawX, drawY);
        ctx.rotate((this.tilt * Math.PI) / 180);
        ctx.translate(-drawX, -drawY);

        // --- 3/4 PERSPECTIVE SETTINGS ---
        const depth = 15; // How much "top" we see
        const heightOffset = 40; // How much it's extruded "up" (Y-axis)
        
        // Base colors based on damage
        let cupboardColor = CONFIG.colors.cupboard;
        if (this.damage >= CONFIG.cupboardDamageThresholds[2]) cupboardColor = '#4B2010';
        else if (this.damage >= CONFIG.cupboardDamageThresholds[1]) cupboardColor = '#6B3410';
        else if (this.damage >= CONFIG.cupboardDamageThresholds[0]) cupboardColor = '#8B4010';

        const darkerColor = '#3d1c07'; // Even darker for sides
        const topColor = '#a65d2e';    // Lighter for top

        // 1. Draw SIDE/THICKNESS (Depth)
        // We simulate depth by drawing a slightly wider/taller back or side
        ctx.fillStyle = darkerColor;
        ctx.fillRect(drawX - this.width / 2, drawY - this.height / 2 - heightOffset, this.width, this.height + heightOffset);

        // 2. Draw TOP SURFACE
        ctx.fillStyle = topColor;
        ctx.fillRect(drawX - this.width / 2, drawY - this.height / 2 - heightOffset, this.width, depth);
        ctx.strokeStyle = '#000';
        ctx.strokeRect(drawX - this.width / 2, drawY - this.height / 2 - heightOffset, this.width, depth);

        // 3. Draw FRONT FACE (This is where the doors are)
        const frontY = drawY - this.height / 2 - heightOffset + depth;
        const frontHeight = this.height + heightOffset - depth;

        ctx.fillStyle = cupboardColor;
        ctx.fillRect(drawX - this.width / 2, frontY, this.width, frontHeight);
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.strokeRect(drawX - this.width / 2, frontY, this.width, frontHeight);

        if (!this.isBroken) {
            // Interior (Visible when doors open)
            if (this.doorAngle > 0) {
                ctx.fillStyle = '#8B6914';
                ctx.fillRect(drawX - this.width / 2 + 2, frontY + 2, this.width - 4, frontHeight - 4);
                
                ctx.fillStyle = '#A0826D';
                ctx.fillRect(drawX - this.width / 2 + 2, frontY + frontHeight / 3, this.width - 4, 2);
                ctx.fillRect(drawX - this.width / 2 + 2, frontY + (frontHeight * 2) / 3, this.width - 4, 2);
            }

            if (this.doorsOpenOutward) {
                // Outward-opening: doors use horizontal skew to simulate 3D opening
                const maxAngle = 70;
                const openAmount = Math.abs(this.doorAngle) / maxAngle;
                const skewAngle = openAmount * 25; // 25 degrees max horizontal skew
                
                // Left door: right edge moves to where left edge was, then apply horizontal skew
                ctx.save();
                const leftDoorClosedX = drawX - this.width / 2;
                const leftDoorOpenX = leftDoorClosedX - this.doorWidth; // Right edge moves to left edge position
                const leftDoorX = lerp(leftDoorClosedX, leftDoorOpenX, openAmount);
                // Move down by half door width only when open
                const leftDoorY = lerp(frontY, frontY + this.doorWidth / 2, openAmount);
                
                // Apply horizontal skew (skewX) to simulate door opening outward
                ctx.translate(leftDoorX, leftDoorY);
                ctx.transform(1, -Math.tan(skewAngle * Math.PI / 180), 0, 1, 0, 0); // Negative for downward skew
                
                ctx.fillStyle = cupboardColor;
                ctx.fillRect(0, 0, this.doorWidth, frontHeight);
                ctx.strokeStyle = '#000';
                ctx.strokeRect(0, 0, this.doorWidth, frontHeight);
                ctx.fillStyle = darkerColor;
                ctx.fillRect(0, 0, 1, frontHeight); // Left edge
                // Handle
                ctx.fillStyle = '#FFD700';
                ctx.beginPath(); ctx.arc(this.doorWidth - 4, frontHeight / 2, 2, 0, Math.PI * 2); ctx.fill();
                ctx.restore();

                // Right door: left edge moves to where right edge was, then apply horizontal skew
                ctx.save();
                const rightDoorClosedX = drawX + this.width / 2 - this.doorWidth;
                const rightDoorOpenX = rightDoorClosedX + this.doorWidth; // Left edge moves to right edge position
                const rightDoorX = lerp(rightDoorClosedX, rightDoorOpenX, openAmount);
                
                // Apply horizontal skew (skewX) - positive for downward skew
                ctx.translate(rightDoorX, frontY);
                ctx.transform(1, Math.tan(skewAngle * Math.PI / 180), 0, 1, 0, 0);
                
                ctx.fillStyle = cupboardColor;
                ctx.fillRect(0, 0, this.doorWidth, frontHeight);
                ctx.strokeStyle = '#000';
                ctx.strokeRect(0, 0, this.doorWidth, frontHeight);
                ctx.fillStyle = darkerColor;
                ctx.fillRect(this.doorWidth - 1, 0, 1, frontHeight); // Right edge
                // Handle
                ctx.fillStyle = '#FFD700';
                ctx.beginPath(); ctx.arc(4, frontHeight / 2, 2, 0, Math.PI * 2); ctx.fill();
                ctx.restore();
            } else {
                // Inward-opening: doors slide/translate outward (original behavior)
                const openAmount = Math.abs(this.doorAngle) / 120;
                const doorOffset = openAmount * (this.doorWidth + 1);

                // Left door
                ctx.save();
                ctx.translate(drawX - this.width / 2 - doorOffset, frontY);
                ctx.fillStyle = cupboardColor;
                ctx.fillRect(0, 0, this.doorWidth, frontHeight);
                ctx.strokeStyle = '#000';
                ctx.strokeRect(0, 0, this.doorWidth, frontHeight);
                ctx.fillStyle = darkerColor;
                ctx.fillRect(this.doorWidth - 1, 0, 1, frontHeight);
                // Handle
                ctx.fillStyle = '#FFD700';
                ctx.beginPath(); ctx.arc(this.doorWidth - 4, frontHeight / 2, 2, 0, Math.PI * 2); ctx.fill();
                ctx.restore();

                // Right door
                ctx.save();
                ctx.translate(drawX + this.width / 2 + doorOffset, frontY);
                ctx.fillStyle = cupboardColor;
                ctx.fillRect(-this.doorWidth, 0, this.doorWidth, frontHeight);
                ctx.strokeStyle = '#000';
                ctx.strokeRect(-this.doorWidth, 0, this.doorWidth, frontHeight);
                ctx.fillStyle = darkerColor;
                ctx.fillRect(-this.doorWidth, 0, 1, frontHeight);
                // Handle
                ctx.fillStyle = '#FFD700';
                ctx.beginPath(); ctx.arc(-this.doorWidth + 4, frontHeight / 2, 2, 0, Math.PI * 2); ctx.fill();
                ctx.restore();
            }
        } else {
            // Broken interior
            ctx.fillStyle = '#8B6914';
            ctx.fillRect(drawX - this.width / 2 + 2, frontY + 2, this.width - 4, frontHeight - 4);
        }

        // Cracks (Adjusted for front face)
        if (this.damage >= CONFIG.cupboardDamageThresholds[0]) {
            ctx.strokeStyle = '#000';
            ctx.lineWidth = 1;
            ctx.globalAlpha = 0.5;
            const numCracks = Math.min(Math.floor(this.damage / 5), 8);
            for (let i = 0; i < numCracks; i++) {
                const x1 = drawX + (Math.random() - 0.5) * this.width;
                const y1 = frontY + (Math.random() - 0.5) * frontHeight;
                ctx.beginPath(); ctx.moveTo(x1, y1);
                ctx.lineTo(x1 + (Math.random() - 0.5) * 20, y1 + (Math.random() - 0.5) * 20);
                ctx.stroke();
            }
            ctx.globalAlpha = 1;
        }

        ctx.restore();
    }
}
